/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useEffect, useState } from "react";

// react-router-dom components
import { Link, Navigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import MuiLink from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";

// @mui icons
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import PinIcon from "@mui/icons-material/Pin";
import PasswordIcon from "@mui/icons-material/Password";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "examples/LayoutContainers/BasicLayout";

// Images
import bgImage from "assets/images/bg-sign-in-basic.jpeg";

// Router
import { useNavigate, useLocation } from "react-router-dom";
import { useApi, useUser } from "../../../context/api.js";
import QRCodeScanner from "../../../components/QRCodeScanner";
import PinInput from "../components/PinInput";

const ERROR_MESSAGES = {
  1: "Veuillez saisir votre nom d'utilisateur.",
  2: "Veuillez saisir votre mot de passe.",
  3: "Code QR invalide.",
  4: "Veuillez saisir votre code temporaire.",
  5: "Code temporaire invalide.",
  6: "Une erreur est survenue pendant l'envoi du code temporaire.",
  400: "Utilisateur ou mot de passe incorrect.",
  401: "Compte désactivé.",
  402: "Denied permission.",
  403: "2-factor authentication code required.",
  404: "Failed to authenticate 2-factor authentication code.",
  406: "Enforce to authenticate with 2-factor authentication code.",
  407: "Blocked IP source.",
  408: "Expired password cannot change.",
  409: "Expired password.",
  410: "Password must be changed.",
};

function Basic() {
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();
  const user = useUser();

  const from = location.state?.from?.pathname || "/";

  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({ open: false });
  const [loginMode, setLoginMode] = useState("password");
  const [totpStep, setTotpStep] = useState(1);
  const [totpUsername, setTotpUsername] = useState(location.state?.username || "");
  const [totpPin, setTotpPin] = useState("");
  const [totpTimer, setTotpTimer] = useState(0);
  const [selectedPosition, setSelectedPosition] = useState(null);

  const handleSubmit = (event) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget.form || event.currentTarget);

    if (loginMode === "password") {
      const username = formData.get("username");
      const password = formData.get("password");

      if (!username) {
        setError({ open: true, code: 1 });

        return;
      }

      if (!password) {
        setError({ open: true, code: 2 });

        return;
      }

      setLoading(true);
      api
        .login(username, password, rememberMe)
        .then(() => {
          navigate(from, { replace: true });
        })
        .catch((error) => {
          setError({ open: true, code: error.code });
          setLoading(false);
        });
    } else if (loginMode === "code") {
      if (event.currentTarget.id === "send-code" || totpStep === 1) {
        // Send totp code
        if (!totpUsername) {
          setError({ open: true, code: 1 });

          return;
        }

        setLoading(true);
        api
          .totpCode(totpUsername)
          .then((result) => {
            setLoading(false);
            if (result) {
              setTotpStep(2);
              setTotpTimer(30);
            } else {
              setError({ open: true, code: 6 });
            }
          })
          .catch((error) => {
            setError({ open: true, code: error.code });
            setLoading(false);
          });
      } else if (totpStep === 2) {
        // Validate totp code
        if (!totpUsername) {
          setError({ open: true, code: 1 });

          return;
        }

        if (!totpPin) {
          setError({ open: true, code: 4 });

          return;
        }

        setLoading(true);
        api
          .totp(totpUsername, totpPin)
          .then((res) => api.loginWithToken(res.token, rememberMe))
          .catch((error) => {
            setError({ open: true, code: 5 });
            setLoading(false);
          });
      }
    }
  };

  useEffect(() => {
    if (totpTimer > 0) {
      const timeout = setTimeout(() => setTotpTimer((t) => t - 1), 1000);

      return () => clearTimeout(timeout);
    }
  }, [totpTimer]);

  const closeError = () => setError((e) => ({ ...e, open: false }));

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const updateSelectedPosition = (c) => {
    if (c) {
      let relativeParent = c;
      while (
        getComputedStyle(relativeParent).position !== "relative" &&
        relativeParent.parentNode
      ) {
        relativeParent = relativeParent.parentNode;
      }
      if (relativeParent) {
        const iconPosition = c.getBoundingClientRect();
        const parentPosition = relativeParent.getBoundingClientRect();

        const BOX_PADDING = 8;
        setSelectedPosition({
          top: iconPosition.top - parentPosition.top - BOX_PADDING - 2,
          left: iconPosition.left - parentPosition.left - BOX_PADDING,
          width: iconPosition.width + 2 * BOX_PADDING,
          height: iconPosition.height + 2 * BOX_PADDING,
        });
      }
    }
  };

  const setMode = (e, mode) => {
    if (e) {
      e.preventDefault();

      if (e.currentTarget) {
        updateSelectedPosition(e.currentTarget);
      }
    }

    setLoginMode(mode);
  };

  const handleTokenLogin = (token) => {
    // Check if valid token
    if (token && token.split(".").length === 3) {
      setLoading(true);
      // Login with token
      api.loginWithToken(token, rememberMe).then((result) => {
        if (!result) {
          setLoading(false);
          setError({ open: true, code: 3 });
        }
      });
    } else {
      setError({ open: true, code: 3 });
    }
  };

  if (user) {
    return <Navigate to="/" replace />;
  }

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Connexion
          </MDTypography>
          <Grid
            container
            spacing={3}
            justifyContent="center"
            sx={{ mt: 1, mb: 2, position: "relative" }}
          >
            <Grid item xs={2}>
              <MDTypography
                component={MuiLink}
                onClick={(e) => setMode(e, "password")}
                variant="body1"
                color="white"
                href="#"
                ref={(c) => !selectedPosition && updateSelectedPosition(c)}
              >
                <Tooltip title={"Se connecter avec un mot de passe."}>
                  <PasswordIcon color="inherit" />
                </Tooltip>
              </MDTypography>
            </Grid>
            <Grid item xs={2}>
              <MDTypography
                component={MuiLink}
                onClick={(e) => setMode(e, "qrcode")}
                variant="body1"
                color="white"
                href="#"
              >
                <Tooltip title={"Se connecter avec un code QR."}>
                  <QrCodeScannerIcon color="inherit" />
                </Tooltip>
              </MDTypography>
            </Grid>
            <Grid item xs={2}>
              <MDTypography
                component={MuiLink}
                onClick={(e) => setMode(e, "code")}
                variant="body1"
                color="white"
                href="#"
              >
                <Tooltip title={"Se connecter à l'aide d'un code temporaire."}>
                  <PinIcon color="inherit" />
                </Tooltip>
              </MDTypography>
            </Grid>
            {selectedPosition && (
              <MDBox
                borderRadius="8px"
                sx={{
                  position: "absolute",
                  border: "2px solid white",
                  pointerEvents: "none",
                  transition: "left 300ms ease-in-out",
                  boxShadow: "0px 0px 3px 0px #FFFFFF",
                  ...selectedPosition,
                }}
              />
            )}
          </Grid>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            {loginMode === "password" && (
              <>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Nom d'utilisateur"
                    name="username"
                    defaultValue={location.state?.username}
                    fullWidth
                    disabled={loading}
                  />
                </MDBox>
                <MDBox mb={2}>
                  <MDInput
                    type="password"
                    label="Mot de passe"
                    name="password"
                    fullWidth
                    disabled={loading}
                  />
                </MDBox>
              </>
            )}
            {loginMode === "qrcode" && (
              <QRCodeScanner onResult={handleTokenLogin} disabled={loading} />
            )}
            {loginMode === "code" && (
              <>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Nom d'utilisateur"
                    name="username"
                    value={totpUsername}
                    onChange={(e) => setTotpUsername(e.currentTarget.value)}
                    fullWidth
                    sx={{ display: totpStep === 1 ? "block" : "none" }}
                    disabled={loading}
                  />
                </MDBox>
                <MDBox mb={2} sx={{ display: totpStep === 2 ? "block" : "none" }}>
                  <PinInput
                    value={totpPin}
                    onChange={(e) => setTotpPin(e.currentTarget.value)}
                    disabled={loading}
                  />
                </MDBox>
              </>
            )}
            <MDBox display="flex" alignItems="center" ml={-1}>
              <Switch checked={rememberMe} onChange={handleSetRememberMe} disabled={loading} />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Rester connecté sur cet appareil
              </MDTypography>
            </MDBox>
            <MDBox mt={4} mb={1}>
              {loginMode === "password" && (
                <MDButton
                  variant="gradient"
                  color="info"
                  type="submit"
                  fullWidth
                  disabled={loading}
                >
                  Se connecter
                </MDButton>
              )}
              {loginMode === "code" && (
                <>
                  <MDButton
                    variant="gradient"
                    color={totpStep === 1 ? "info" : "secondary"}
                    type="submit"
                    id="send-code"
                    fullWidth
                    sx={{ mb: 2 }}
                    disabled={loading || totpTimer > 0}
                    onClick={handleSubmit}
                  >
                    Envoyer un {totpStep === 2 ? "nouveau" : ""} code{" "}
                    {totpTimer > 0 ? "(" + totpTimer + ")" : ""}
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="info"
                    type="submit"
                    fullWidth
                    disabled={loading || totpStep !== 2 || !totpPin.match(/^[0-9]{6}$/)}
                  >
                    Valider le code
                  </MDButton>
                </>
              )}
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Mot de passe oublié&nbsp;?{" "}
                <MDTypography
                  component={Link}
                  to="/reset-password"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Cliquez ici
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
      <Snackbar
        open={error.open}
        autoHideDuration={5000}
        onClose={closeError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert variant="filled" severity="error">
          {ERROR_MESSAGES[error.code] || ERROR_MESSAGES[400]}
        </Alert>
      </Snackbar>
    </BasicLayout>
  );
}

export default Basic;
